import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Plus, Briefcase, Target, Clock, Pencil, Trash2, CheckSquare } from 'lucide-react';

const API_URL = window.location.origin || '';

const ProjectsSection = ({ currentTeam, dashboardArea, projectTypes = [] }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentTeam && dashboardArea) {
      fetchProjects();
    }
  }, [currentTeam, dashboardArea]);

  const fetchProjects = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/projects?dashboard_area=${dashboardArea}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="text-center py-12 text-gray-600">Loading projects...</div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Projects</h2>
        <CreateProjectDialog
          currentTeam={currentTeam}
          dashboardArea={dashboardArea}
          projectTypes={projectTypes}
          onProjectCreated={fetchProjects}
        />
      </div>
      
      {projects.length === 0 ? (
        <div className="text-center py-12 text-gray-900">
          <Briefcase className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="font-medium">No projects yet.</p>
          <p className="text-sm mt-2 text-gray-600">
            Create projects to track multi-step initiatives and goals
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {projects.map(project => (
            <ProjectCard
              key={project.id}
              project={project}
              currentTeam={currentTeam}
              onProjectUpdated={fetchProjects}
            />
          ))}
        </div>
      )}
    </Card>
  );
};

// Project Card Component
const ProjectCard = ({ project, currentTeam, onProjectUpdated }) => {
  const [showActivities, setShowActivities] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateTaskDialog, setShowCreateTaskDialog] = useState(false);
  const [newActivity, setNewActivity] = useState('');
  const [projectTasks, setProjectTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: project.title || '',
    description: project.description || '',
    status: project.status || 'not_started',
    priority: project.priority || 'medium',
    target_date: project.target_date ? new Date(project.target_date).toISOString().split('T')[0] : ''
  });

  // Fetch tasks for this project
  const fetchProjectTasks = async () => {
    setLoadingTasks(true);
    try {
      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/tasks`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const allTasks = await response.json();
        // Filter tasks that belong to this project
        const filtered = allTasks.filter(task => task.project_id === project.id);
        setProjectTasks(filtered);
      }
    } catch (error) {
      console.error('Error fetching project tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  useEffect(() => {
    if (showTasks) {
      fetchProjectTasks();
    }
  }, [showTasks]);

  const addActivity = async () => {
    if (!newActivity.trim()) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/projects/${project.id}/activity`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ description: newActivity })
        }
      );

      if (response.ok) {
        setNewActivity('');
        toast.success('Activity logged');
        onProjectUpdated();
      } else {
        toast.error('Failed to log activity');
      }
    } catch (error) {
      console.error('Error logging activity:', error);
      toast.error('Failed to log activity');
    }
  };

  const updateProject = async () => {
    try {
      const updateData = {
        ...editFormData,
        target_date: editFormData.target_date ? new Date(editFormData.target_date).toISOString() : null
      };

      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/projects/${project.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(updateData)
        }
      );

      if (response.ok) {
        toast.success('Project updated');
        setShowEditDialog(false);
        onProjectUpdated();
      } else {
        toast.error('Failed to update project');
      }
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Failed to update project');
    }
  };

  const deleteProject = async () => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/projects/${project.id}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        toast.success('Project deleted');
        onProjectUpdated();
      } else {
        toast.error('Failed to delete project');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-900 border-green-300';
      case 'in_progress': return 'bg-blue-100 text-blue-900 border-blue-300';
      case 'on_hold': return 'bg-amber-100 text-amber-900 border-amber-300';
      default: return 'bg-gray-100 text-gray-900 border-gray-300';
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-900 border-red-300';
      case 'medium': return 'bg-amber-100 text-amber-900 border-amber-300';
      default: return 'bg-gray-100 text-gray-900 border-gray-300';
    }
  };

  return (
    <>
      <Card className="p-4 border-2">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-gray-900">{project.title}</h3>
            <div className="flex gap-2 mt-2">
              <Badge className={getStatusBadgeColor(project.status)}>
                {project.status.replace('_', ' ')}
              </Badge>
              <Badge className={getPriorityBadgeColor(project.priority)}>
                {project.priority} priority
              </Badge>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowEditDialog(true)}
              className="text-gray-900"
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={deleteProject}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {project.description && (
          <p className="text-gray-700 mb-3">{project.description}</p>
        )}

        {project.target_date && (
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <Target className="w-4 h-4" />
            <span>Target: {new Date(project.target_date).toLocaleDateString()}</span>
          </div>
        )}

        {/* Tasks Section */}
        <div className="border-t-2 pt-3 mt-3">
          <div className="flex justify-between items-center mb-2">
            <button
              onClick={() => setShowTasks(!showTasks)}
              className="flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-blue-600"
            >
              <CheckSquare className="w-4 h-4" />
              <span>Tasks ({projectTasks.length})</span>
            </button>
            <Button
              size="sm"
              onClick={() => setShowCreateTaskDialog(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Task
            </Button>
          </div>

          {showTasks && (
            <div className="space-y-2 mt-2">
              {loadingTasks ? (
                <p className="text-sm text-gray-500">Loading tasks...</p>
              ) : projectTasks.length === 0 ? (
                <p className="text-sm text-gray-500">No tasks yet. Create a task to get started.</p>
              ) : (
                projectTasks.map(task => (
                  <div key={task.id} className="bg-gray-50 p-2 rounded border">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{task.title}</p>
                        {task.description && (
                          <p className="text-xs text-gray-600 mt-1">{task.description}</p>
                        )}
                      </div>
                      <Badge className="text-xs ml-2">
                        {task.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Activity Log Section */}
        <div className="border-t-2 pt-3 mt-3">
          <button
            onClick={() => setShowActivities(!showActivities)}
            className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-2 hover:text-blue-600"
          >
            <Clock className="w-4 h-4" />
            <span>Activity Log ({(project.activity_log || []).length})</span>
          </button>

          {showActivities && (
            <div className="space-y-3 mt-3">
              {/* Add Activity Form */}
              <div className="flex gap-2">
                <Input
                  value={newActivity}
                  onChange={(e) => setNewActivity(e.target.value)}
                  placeholder="Add activity note..."
                  className="text-gray-900 border-2 flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && addActivity()}
                />
                <Button 
                  onClick={addActivity}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Log
                </Button>
              </div>

              {/* Activity Timeline */}
              {(project.activity_log || []).length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {[...(project.activity_log || [])].reverse().map((activity, idx) => (
                    <div key={idx} className="border-l-4 border-blue-600 pl-3 py-2 bg-gray-50">
                      <div className="flex justify-between items-start">
                        <p className="text-sm text-gray-900">{activity.description}</p>
                        <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                          {new Date(activity.date).toLocaleDateString()} {new Date(activity.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Edit Project Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-title" className="text-gray-900">Title *</Label>
              <Input
                id="edit-title"
                value={editFormData.title}
                onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                className="text-gray-900 border-2"
              />
            </div>

            <div>
              <Label htmlFor="edit-description" className="text-gray-900">Description</Label>
              <Textarea
                id="edit-description"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                rows={3}
                className="text-gray-900 border-2"
              />
            </div>

            <div>
              <Label htmlFor="edit-status" className="text-gray-900">Status</Label>
              <select
                id="edit-status"
                className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
                value={editFormData.status}
                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
              >
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div>
              <Label htmlFor="edit-priority" className="text-gray-900">Priority</Label>
              <select
                id="edit-priority"
                className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
                value={editFormData.priority}
                onChange={(e) => setEditFormData({ ...editFormData, priority: e.target.value })}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div>
              <Label htmlFor="edit-target-date" className="text-gray-900">Target Date</Label>
              <Input
                id="edit-target-date"
                type="date"
                value={editFormData.target_date}
                onChange={(e) => setEditFormData({ ...editFormData, target_date: e.target.value })}
                className="text-gray-900 border-2"
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={updateProject}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                Save Changes
              </Button>
              <Button
                onClick={() => setShowEditDialog(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog */}
      <CreateTaskForProjectDialog
        open={showCreateTaskDialog}
        onOpenChange={setShowCreateTaskDialog}
        currentTeam={currentTeam}
        project={project}
        onTaskCreated={() => {
          fetchProjectTasks();
          onProjectUpdated();
        }}
      />
    </>
  );
};

// Create Project Dialog
const CreateProjectDialog = ({ currentTeam, dashboardArea, projectTypes, onProjectCreated }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'not_started',
    priority: 'medium',
    target_date: ''
  });

  const defaultProjectTypes = ['New Project', 'Initiative', 'Goal', 'Other'];
  const types = projectTypes.length > 0 ? projectTypes : defaultProjectTypes;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const projectData = {
        ...formData,
        dashboard_area: dashboardArea,
        target_date: formData.target_date ? new Date(formData.target_date).toISOString() : null
      };

      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/projects`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(projectData)
        }
      );

      if (response.ok) {
        toast.success('Project created successfully');
        setOpen(false);
        setFormData({ title: '', description: '', status: 'not_started', priority: 'medium', target_date: '' });
        onProjectCreated();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create project');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Create Project
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="project-title" className="text-gray-900">Project Type *</Label>
            <select
              id="project-title"
              className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            >
              <option value="">Select project type...</option>
              {types.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="description" className="text-gray-900">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the project"
              rows={3}
              className="text-gray-900 border-2"
            />
          </div>

          <div>
            <Label htmlFor="status" className="text-gray-900">Status</Label>
            <select
              id="status"
              className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="on_hold">On Hold</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div>
            <Label htmlFor="priority" className="text-gray-900">Priority</Label>
            <select
              id="priority"
              className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div>
            <Label htmlFor="target-date" className="text-gray-900">Target Date (Optional)</Label>
            <Input
              id="target-date"
              type="date"
              value={formData.target_date}
              onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
              className="text-gray-900 border-2"
            />
          </div>

          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            Create Project
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Create Task for Project Dialog
const CreateTaskForProjectDialog = ({ open, onOpenChange, currentTeam, project, onTaskCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    assigned_to: '',
    dashboard_area: project.dashboard_area || 'medical'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const taskData = {
        ...formData,
        project_id: project.id, // Link task to project
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        assigned_to: formData.assigned_to || null
      };

      const response = await fetch(
        `${API_URL}/api/teams/${currentTeam.id}/tasks`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(taskData)
        }
      );

      if (response.ok) {
        toast.success('Task created and linked to project');
        onOpenChange(false);
        setFormData({ 
          title: '', 
          description: '', 
          priority: 'medium', 
          due_date: '', 
          assigned_to: '',
          dashboard_area: project.dashboard_area || 'medical'
        });
        onTaskCreated();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create task');
      }
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Task for: {project.title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="task-title" className="text-gray-900">Task Title *</Label>
            <Input
              id="task-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Schedule appointment"
              className="text-gray-900 border-2"
              required
            />
          </div>

          <div>
            <Label htmlFor="task-description" className="text-gray-900">Description</Label>
            <Textarea
              id="task-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add details about this task..."
              rows={3}
              className="text-gray-900 border-2"
            />
          </div>

          <div>
            <Label htmlFor="task-priority" className="text-gray-900">Priority</Label>
            <select
              id="task-priority"
              className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div>
            <Label htmlFor="task-due-date" className="text-gray-900">Due Date</Label>
            <Input
              id="task-due-date"
              type="datetime-local"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              className="text-gray-900 border-2"
            />
          </div>

          <div className="flex gap-2">
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
              Create Task
            </Button>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectsSection;
